/**
 * Coerce AI verification anchors/gaps to plain strings.
 * Gemini sometimes returns factSheet-shaped objects; ReportEditor must never render those as React children.
 */

export function formatVerificationItem(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item.trim();
  if (typeof item === 'number' || typeof item === 'boolean') return String(item);
  if (typeof item === 'object') {
    if (typeof item.proves === 'string' && item.proves.trim()) return item.proves.trim();
    if (typeof item.evidenceSummary === 'string' && item.evidenceSummary.trim()) {
      const fileName = typeof item.fileName === 'string' && item.fileName.trim() ? item.fileName.trim() : '';
      return fileName ? `${fileName}: ${item.evidenceSummary.trim()}` : item.evidenceSummary.trim();
    }
    if (typeof item.fileName === 'string' && item.fileName.trim()) return item.fileName.trim();
    if (typeof item.text === 'string' && item.text.trim()) return item.text.trim();
    if (typeof item.summary === 'string' && item.summary.trim()) return item.summary.trim();
    if (typeof item.gap === 'string' && item.gap.trim()) return item.gap.trim();
    try {
      return JSON.stringify(item);
    } catch {
      return '';
    }
  }
  return '';
}

export function normalizeVerificationList(list) {
  if (!Array.isArray(list)) {
    if (list == null || list === '') return [];
    const one = formatVerificationItem(list);
    return one ? [one] : [];
  }
  return list.map(formatVerificationItem).filter(Boolean);
}

export function normalizeStationVerification(verification) {
  const src = verification && typeof verification === 'object' ? verification : {};
  return {
    anchors: normalizeVerificationList(src.anchors),
    gaps: normalizeVerificationList(src.gaps),
  };
}

/** Mutates stationAnalysis stations in place; returns the same object. */
export function normalizeStationAnalysisVerifications(stationAnalysis) {
  if (!stationAnalysis || typeof stationAnalysis !== 'object') return stationAnalysis;
  Object.keys(stationAnalysis).forEach((sId) => {
    const station = stationAnalysis[sId];
    if (!station || typeof station !== 'object') return;
    if (station.verification != null || station.anchors != null || station.gaps != null) {
      // Extraction shape may be { anchors, gaps } at station root (refinery) or under .verification (draft)
      if (station.verification && typeof station.verification === 'object') {
        station.verification = normalizeStationVerification(station.verification);
      } else if (Array.isArray(station.anchors) || Array.isArray(station.gaps)) {
        station.verification = normalizeStationVerification(station);
        delete station.anchors;
        delete station.gaps;
      }
    }
  });
  return stationAnalysis;
}

/** Refinery extraction: stations map values are { anchors, gaps } (not full stationAnalysis rows). */
export function normalizeExtractionStations(stations) {
  if (!stations || typeof stations !== 'object') return stations || {};
  const out = {};
  Object.keys(stations).forEach((sId) => {
    out[sId] = normalizeStationVerification(stations[sId]);
  });
  return out;
}